import { AnimatePresence, motion } from 'framer-motion';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemeMode = 'light' | 'dark' | 'auto';
export type Surface = 'editorial' | 'platform' | 'video';
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeContextValue {
  theme: ThemeMode;
  surface: Surface;
  setTheme: (theme: ThemeMode) => void;
  resolvedTheme: ResolvedTheme;
}

const STORAGE_KEY = 'archetelos-theme';

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredTheme(defaultTheme: ThemeMode): ThemeMode {
  if (typeof window === 'undefined') return defaultTheme;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'auto') return stored;
  return defaultTheme;
}

function getSystemPreference(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(theme: ThemeMode, surface: Surface, _system: ResolvedTheme): ResolvedTheme {
  // Explicit user choice always wins (the settings toggle sets theme to
  // 'light' | 'dark').
  if (theme === 'light' || theme === 'dark') return theme;
  // 'auto' resolves by surface. App ('platform') surfaces default to LIGHT —
  // NOT system preference. Phase G surfaced unreadable dark-on-dark for users
  // whose OS is dark; app dark variants are incomplete by design. Dark is
  // opt-in via the settings toggle only. Video forces dark; editorial forces
  // light. System preference is intentionally not consulted here.
  switch (surface) {
    case 'editorial':
      return 'light';
    case 'video':
      return 'dark';
    case 'platform':
      return 'light';
    default:
      return 'light';
  }
}

export interface ThemeProviderProps {
  children: ReactNode;
  surface?: Surface;
  defaultTheme?: ThemeMode;
}

export function ThemeProvider({
  children,
  surface = 'platform',
  defaultTheme = 'auto',
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeMode>(defaultTheme);
  const [system, setSystem] = useState<ResolvedTheme>('light');
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setThemeState(readStoredTheme(defaultTheme));
    setSystem(getSystemPreference());
    setHydrated(true);
  }, [defaultTheme]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (event: MediaQueryListEvent) => setSystem(event.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const resolvedTheme = useMemo(
    () => resolveTheme(theme, surface, system),
    [theme, surface, system],
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (resolvedTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    root.dataset.surface = surface;
  }, [resolvedTheme, surface]);

  const setTheme = useCallback((next: ThemeMode) => {
    setThemeState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, surface, setTheme, resolvedTheme }),
    [theme, surface, setTheme, resolvedTheme],
  );

  return (
    <ThemeContext.Provider value={value}>
      {hydrated ? (
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={resolvedTheme}
            initial={{ opacity: 0.85 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      ) : (
        children
      )}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}
